/*!
 * Collection navigator
 *
 * Doua lucruri importante:
 *  - toate cautarile sunt scoped pe radacina blocului, nu pe `document`. Atributele
 *    `data-menu-image` / `data-menu-text` sunt identice intre instante, deci un
 *    querySelector global ar returna mereu prima instanta din pagina.
 *  - imaginile de hover nu au `src` la randare, ci `data-src`. Fiind pozitionate
 *    absolut cu `opacity: 0`, browserul le-ar descarca pe toate odata ce sectiunea
 *    intra in viewport: 9 fisiere ca sa se vada unul. Se incarca la prima
 *    interactiune cu zona de meniuri.
 */
(function () {
  const SELECTOR = '[data-collection-navigator]';

  class CollectionNavigator {
    constructor(root) {
      this.root = root;
      this.menuLinks = Array.from(root.querySelectorAll('.collection-navigator__menu-link'));
      this.mainImage = root.querySelector('[data-main-image]');
      this.images = Array.from(root.querySelectorAll('.collection-navigator__image'));
      this.descTexts = Array.from(root.querySelectorAll('.collection-navigator__description-text'));
      this.menuContainer = root.querySelector('.collection-navigator__menus');

      if (!this.menuLinks.length || !this.mainImage) return;

      this.defaultImage = root.querySelector('.collection-navigator__image.is-default');
      this.defaultText = root.querySelector('.collection-navigator__description-text.is-default');
      this.defaultId = this.menuLinks[0].getAttribute('data-image-id');

      this.imagesLoaded = false;
      this.touchPreviewId = null;
      this.isTouch = window.matchMedia('(hover: none)').matches;

      this.bind();
      // Starea initiala: primul link marcat, ca sa corespunda cu imaginea
      // implicita deja randata in HTML.
      this.activate(this.defaultId);
    }

    // Muta `data-src`/`data-srcset` in atributele reale, o singura data.
    loadImages() {
      if (this.imagesLoaded) return;
      this.imagesLoaded = true;

      this.images.forEach((img) => {
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          delete img.dataset.srcset;
        }
        if (img.dataset.src) {
          img.src = img.dataset.src;
          delete img.dataset.src;
        }
      });
    }

    bind() {
      // Prima intentie de interactiune declanseaza incarcarea, indiferent daca
      // vine de la mouse, tastatura sau atingere.
      const warmUp = () => this.loadImages();
      if (this.menuContainer) {
        this.menuContainer.addEventListener('pointerenter', warmUp, { once: true });
        this.menuContainer.addEventListener('focusin', warmUp, { once: true });
        this.menuContainer.addEventListener('touchstart', warmUp, { once: true, passive: true });

        // Nu resetam la iesirea cursorului: ultimul link atins ramane activ, cu
        // imaginea si descrierea lui. Altfel continutul ar sari inapoi la primul
        // link de fiecare data cand mouse-ul paraseste zona.
      }

      this.menuLinks.forEach((link) => {
        const id = link.getAttribute('data-image-id');

        // Tastatura: acelasi comportament ca hover-ul, altfel cine navigheaza cu
        // Tab nu vede niciodata imaginea si descrierea asociate linkului.
        link.addEventListener('focus', () => {
          this.loadImages();
          this.activate(id);
        });

        if (this.isTouch) {
          // Primul tap previzualizeaza, al doilea pe acelasi link navigheaza.
          link.addEventListener('click', (event) => {
            if (this.touchPreviewId === id) return;
            event.preventDefault();
            this.loadImages();
            this.activate(id);
            this.touchPreviewId = id;
          });
          return;
        }

        link.addEventListener('mouseenter', () => {
          this.loadImages();
          this.activate(id);
        });
      });
    }

    activate(imageId) {
      const hoverImage = imageId ? this.root.querySelector(`[data-menu-image="${imageId}"]`) : null;
      const targetImage = hoverImage || this.defaultImage || this.mainImage;
      this.images.forEach((img) => img.classList.toggle('hidden', img !== targetImage));
      this.mainImage.classList.toggle('hidden', this.mainImage !== targetImage);

      const hoverText = imageId ? this.root.querySelector(`[data-menu-text="${imageId}"]`) : null;
      const targetText = hoverText || this.defaultText;
      this.descTexts.forEach((text) => text.classList.toggle('is-inactive', text !== targetText));

      this.menuLinks.forEach((link) => {
        link.classList.toggle('is-active', link.getAttribute('data-image-id') === imageId);
      });
    }
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTOR).forEach((el) => {
      if (el.dataset.navigatorReady === 'true') return;
      el.dataset.navigatorReady = 'true';
      new CollectionNavigator(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => init(event.target));
})();
